import { MouseEvent, ReactNode, useEffect, useState } from 'react';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';

import SharedIconButton from './buttons/shared-icon-button';

export interface SharedProfileInformationAccordionProps {
  title: string;
  count?: number;
  hasError?: boolean;
  disabled?: boolean;
  defaultExpanded?: boolean;
  onAdd?: (event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>) => void;
  actions?: ReactNode;
  children: ReactNode;
}

export default function SharedProfileInformationAccordion({
  title,
  count,
  hasError = false,
  disabled = false,
  defaultExpanded = false,
  onAdd,
  actions,
  children,
}: SharedProfileInformationAccordionProps) {
  const theme = useTheme();
  const semanticTheme = theme.palette.semantic;
  const [open, setOpen] = useState(defaultExpanded);

  useEffect(() => {
    if (count !== undefined) {
      setOpen(count > 0);
    }
  }, [count]);

  useEffect(() => {
    if (hasError) {
      setOpen(true);
    }
  }, [hasError]);

  return (
    <Accordion
      expanded={open}
      elevation={0}
      disableGutters
      onChange={() => {
        if (count === 0) return;
        setOpen(!open);
      }}
      sx={{
        border: `0.5px solid ${semanticTheme.outline.light}`,
        borderRadius: '4px',
        '&::before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton component="span" disabled={count === 0} disableRipple>
            <ExpandMore />
          </IconButton>
        }
        sx={{
          backgroundColor: semanticTheme.surface.bg1,
          borderRadius: '4px',
          '&.Mui-expanded': {
            minHeight: 48,
            borderRadius: '4px 4px 0 0',
          },
        }}
      >
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography
            sx={{
              color: hasError
                ? semanticTheme.text.error2
                : semanticTheme.text.title2,
            }}
          >
            {title} {count !== undefined && count > 0 && `(${count})`}
          </Typography>

          {onAdd && (
            <SharedIconButton
              variant="add"
              tooltipLabel="Add"
              component="span"
              size="small"
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                onAdd(event);
              }}
              sx={{ color: semanticTheme.text.title2 }}
            />
          )}

          {actions}
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          pt: 2,
          borderTop: 0.5,
          borderColor: semanticTheme.outline.light,
          borderRadius: '0 0 4px 4px',
          backgroundColor: semanticTheme.surface.bg1,
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}
